import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { companionsKeys } from './companions';
import { bagKeys } from './bag';
import { scheduleRewardRefetch } from './rewardRefetch';
// Type-only, circular with companions.ts (see that file's own note) — safe, erased at compile time.
import type { CompanionDto } from './companions';

// Server: CareDto record (pets/CareDto.java) — hunger/joy/grooming are always the DECAYED-TO-NOW
// value, never the raw stored stat; thriving is true only when all three are >= 70; playReadyAt
// is null whenever the free play action is available right now.
export type CareDto = {
  hunger: number;
  joy: number;
  grooming: number;
  thriving: boolean;
  playReadyAt: string | null;
};

export type CareAction = 'feed' | 'play' | 'groom' | 'poke';

export type CareInput = {
  action: CareAction;
  itemCode?: string;
};

export type CareResponse = {
  care: CareDto;
};

export const postCareAction = (companionId: number, input: CareInput) =>
  apiFetch<CareResponse>(`/companions/${companionId}/care`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

// Mirrors CareService.FREE_PLAY_JOY / the 0-100 clamp server-side (server/src/main/java/com/
// projectpets/pets/CareService.java) — the only boost amount this client can predict in advance.
const FREE_PLAY_JOY = 30;
const CARE_MAX = 100;

/** The three care stats a `care_restore` item can name, keyed by the server's own
 *  `effect.care` keys (item.schema.json constrains them to exactly these). */
export const CARE_STATS: { key: 'hunger' | 'joy' | 'grooming'; label: string }[] = [
  { key: 'hunger', label: 'Hunger' },
  { key: 'joy', label: 'Joy' },
  { key: 'grooming', label: 'Grooming' },
];

/** §11.10 disabled-with-reason for a care item whose stat is already full — the server would
 *  consume it for nothing (QA 2026-09-23, economy F10). Null when the action is worth taking, or
 *  when the caller cannot tell (no amounts on the wire, no companion). Shared by
 *  `ItemDetailSheet` and `CareTray`, which face the same rows. */
export function careFullReason(
  item: { category: string; effectAmounts?: Record<string, number> | null },
  companion: CompanionDto | null,
): string | null {
  const amounts = item.effectAmounts;
  if (!companion || !amounts || item.category !== 'consumable') return null;
  const named = CARE_STATS.filter((s) => amounts[s.key] != null);
  if (named.length === 0 || named.some((s) => companion.care[s.key] < CARE_MAX)) return null;
  const key = named[0].key;
  if (key === 'hunger') return `${companion.name} isn't hungry right now`;
  if (key === 'grooming') return `${companion.name} is already well groomed`;
  return `${companion.name} is already full of joy`;
}

/**
 * Optimistic care action against the `companions` list cache (mirrors usePutOutfit's onMutate/
 * onError/onSettled shape) — but ONLY for the free `play` action (`action === 'play'` with no
 * `itemCode`). That's the single case whose boost is a fixed, client-known constant (+30 joy,
 * clamped at 100). `feed`, `groom`, and `play` WITH an itemCode (a toy) all apply whatever the
 * consumed item's own `care_restore` effect map says — data this client never has — so those
 * settle on the server's response instead of the cache visibly "popping" from a guessed number
 * back to the real one. Callers can still read `isPending` for those cases to show a pending
 * state without an optimistic value.
 */
export function useCareAction(companionId: number) {
  const queryClient = useQueryClient();
  const key = companionsKeys.all;
  return useMutation({
    mutationFn: (input: CareInput) => postCareAction(companionId, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CompanionDto[]>(key);
      if (previous && input.action === 'play' && !input.itemCode) {
        queryClient.setQueryData<CompanionDto[]>(
          key,
          previous.map((c) =>
            c.id === companionId
              ? { ...c, care: { ...c.care, joy: Math.min(CARE_MAX, c.care.joy + FREE_PLAY_JOY) } }
              : c),
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    // Fix round 1 (review finding, HIGH): `feed`/`groom`/`play`-with-an-itemCode all genuinely
    // consume a bag item server-side (CareService's `consumeItem` path) — leaving `bagKeys.all`
    // un-invalidated meant BagScreen's grid kept offering an item the account no longer owned
    // (a qty-1 stack stayed visible; a stack's qty badge didn't decrement), so a second tap on
    // the same item hit a confusing `not_owned` with no visible cause. Only on SUCCESS (a failed
    // care action, e.g. `wrong_stage`, never consumes anything) and only when this specific call
    // actually named an item — the free `play` action (no itemCode) never touches the bag at all,
    // matching `useUseItem`'s own bag-invalidation-on-success precedent in `bag.ts`.
    onSuccess: (_data, input) => {
      if (input.itemCode) queryClient.invalidateQueries({ queryKey: bagKeys.all });
      // QA 2026-09-23 bug 1: the daily/quest reward this action may complete lands via the
      // outbox AFTER this response — the onSettled refetch below is too early to see it.
      scheduleRewardRefetch(queryClient);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}
